import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { displayProductName } from "@/lib/productName";

export default function DashSubscriptions() {
  const [subs, setSubs] = React.useState(null);
  const [portalBusy, setPortalBusy] = React.useState(false);
  const { refresh } = useAuth();
  const { t } = useLang();

  const load = () => api.get("/api/dashboard/subscriptions").then((r) => setSubs(r.data.items));
  React.useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    try {
      await api.post(`/api/billing/cancel/${id}`);
      toast.success(t("common.updated"));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/api/creem/portal");
      if (data.portal_url) window.location.href = data.portal_url;
      else toast.error("Portal URL missing");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setPortalBusy(false);
  };

  if (!subs) return <div className="h-40 lp-shimmer rounded-2xl" />;

  return (
    <div className="space-y-6" data-testid="dash-subs-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl text-white/95">{t("dash.subs.title")}</h1>
          <p className="text-white/50 text-sm mt-1">{t("dash.subs.subtitle")}</p>
        </div>
        <button onClick={openPortal} disabled={portalBusy} className="lp-btn-secondary" data-testid="creem-portal-btn">
          <ExternalLink className="w-4 h-4" /> {t("dash.subs.portal")}
        </button>
      </div>
      {subs.length === 0 && (
        <div className="lp-card p-10 text-center">
          <div className="text-white/70">{t("dash.subs.empty_title")}</div>
          <Link to="/marketplace" className="lp-btn-primary inline-flex mt-4">{t("dash.subs.browse")}</Link>
        </div>
      )}
      <div className="grid gap-4">
        {subs.map((s) => (
          <div key={s.id} className="lp-card p-5" data-testid={`sub-${s.product?.slug}`}>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="text-white/90 font-medium text-lg">{displayProductName(s.product?.name) || "—"}</div>
                <div className="text-xs text-white/50 mt-0.5">
                  {s.plan?.display_name} · {s.plan?.monthly_requests?.toLocaleString()} {t("landing.pricing.reqs")} · {s.plan?.rate_limit_per_second} {t("landing.pricing.rate")}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {(() => {
                  const scheduled = s.cancel_at_period_end && s.status !== "canceled" && s.status !== "expired";
                  const cancelsIso = s.cancels_at || s.period_end;
                  const cancelsAtLabel = cancelsIso ? new Date(cancelsIso).toLocaleDateString() : null;
                  if (scheduled) {
                    return (
                      <span className="lp-chip amber" data-testid={`sub-badge-${s.product?.slug}`}>
                        {t("dash.subs.badge.cancels_at_period_end")}{cancelsAtLabel ? ` · ${cancelsAtLabel}` : ""}
                      </span>
                    );
                  }
                  const chip = s.status === "active" || s.status === "trialing" ? "green" : "red";
                  return <span className={`lp-chip ${chip}`} data-testid={`sub-badge-${s.product?.slug}`}>{t(`dash.subs.badge.${s.status}`)}</span>;
                })()}
                <span className="lp-chip gold uppercase text-[10px]">{s.tier}</span>
              </div>
            </div>
            <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
              <div className="lp-glass p-3">
                <div className="text-[10px] uppercase tracking-widest text-white/40">{t("dash.subs.used")}</div>
                <div className="text-white/90">{s.monthly_requests_used?.toLocaleString()} {t("landing.pricing.reqs")}</div>
              </div>
              <div className="lp-glass p-3">
                <div className="text-[10px] uppercase tracking-widest text-white/40">{t("dash.subs.limit")}</div>
                <div className="text-white/90">{s.monthly_requests_limit?.toLocaleString()} {t("landing.pricing.reqs")}</div>
              </div>
              <div className="lp-glass p-3">
                <div className="text-[10px] uppercase tracking-widest text-white/40">{t("dash.subs.period_end")}</div>
                <div className="text-white/90">{s.period_end ? new Date(s.period_end).toLocaleDateString() : "—"}</div>
              </div>
            </div>
            <div className="mt-4 flex gap-2 items-center flex-wrap">
              <Link to={`/products/${s.product?.slug}`} className="lp-btn-secondary">{t("dash.subs.upgrade")}</Link>
              {/* Cancel is only meaningful for PAID plans (Pro / Enterprise).
                  Trials are auto-provisioned free entitlements that end with
                  the billing period on their own — showing "Cancel" on them
                  is confusing and produces zero-value clicks. */}
              {s.tier !== "trial" && (s.status === "active" || s.status === "trialing") && !s.cancel_at_period_end && (
                <button onClick={() => cancel(s.id)} className="lp-btn-secondary text-red-300 border-red-400/20" data-testid={`cancel-${s.product?.slug}`}>
                  {t("dash.subs.cancel")}
                </button>
              )}
              {s.tier === "trial" && !s.cancel_at_period_end && (
                <div className="text-xs text-white/50" data-testid={`trial-note-${s.product?.slug}`}>
                  {t("dash.subs.trial_note")}
                </div>
              )}
              {s.cancel_at_period_end && s.status !== "canceled" && s.status !== "expired" && (
                <div className="text-xs text-amber-300/90 flex items-center gap-1" data-testid={`cancel-note-${s.product?.slug}`}>
                  {t("dash.subs.cancel_note")}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
